import { largeCurrencyFormat, oneDecimalPercentFormat } from 'common/formats/formats';
import { ICell as Cell } from 'models/interfaces/spreadsheet-types/core-types';
import {
  SHEET_ALIASES_CONSTANTS,
  SHEET_TITLES_CONSTANTS,
} from 'pages/Valuations/approaches/guidelinePublicCompanies/PerformanceMetrics/common/constants/performanceMetrics';
import { PERFORMANCE_METRICS_SPREADSHEET_RANK_ALIAS } from 'pages/Valuations/approaches/guidelinePublicCompanies/PerformanceMetrics/common/constants/performanceMetrics/sheetAliases';
import {
  CellParserParams,
  CustomParserParams,
  GetRankExpressionParams,
} from 'pages/ValuationsAllocation/approaches/TransactionComps/PerformanceMetrics/types';
import { generateNTMRevenueGrowthExpression } from 'pages/ValuationsAllocation/common/createPerformanceMetrics/helpers';
import { generateAllRowsExpression, getExpression, getObjectValue } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';

const { PERFORMANCE_METRICS_SPREADSHEET_HEADER_SUBTITLE, PERFORMANCE_METRICS_SPREADSHEET_HEADER_TITLE }
  = SHEET_ALIASES_CONSTANTS;
const { PERFORMANCE_METRICS_SPREADSHEET_COLUMNS, PERFORMANCE_METRICS_SPREADSHEET_HEADERS } = SHEET_TITLES_CONSTANTS;

const EXCLUDE_TITLE = 1;

function getRankExpression({ allTransactionsExpression }: GetRankExpressionParams) {
  let expr = null;
  let hidden = true;
  if (allTransactionsExpression.length > 0) {
    // The array should not include the company being valued...
    const lastTransaction = allTransactionsExpression.at(-1);
    const lastRowNumber = lastTransaction ? Number(lastTransaction.slice(1)) : 0;
    const companyRowNumber = `@${lastRowNumber + 1}`;
    // ... but the rank should be calculated based on it
    expr = `=PERCENTRANK(FILTER_ZEROS_AS_OBJECT([${allTransactionsExpression}]), ${companyRowNumber})`;
    hidden = false;
  }
  return { expr, hidden };
}

const cellParser = (params: CellParserParams) => {
  const { alphabet, colIndex, column, row, rowIndex, allTransactionsExpression } = params;

  const rowNumber = rowIndex + 1;
  const colNumber = colIndex + 1;
  const columnLegend = alphabet[colIndex];
  const key = columnLegend + rowNumber;

  let { expr = '', format = null, gridType = 'string', hidden } = getObjectValue(row);
  const { alias, className = '', colSpan = 1, readOnly, rowSpan, useScalarSpreadsheetCell } = getObjectValue(row);
  let value = column[alias]?.value ?? '';
  let customKey;

  // Parse cell based on Row alias
  switch (alias) {
    // Header titles
    case PERFORMANCE_METRICS_SPREADSHEET_HEADER_TITLE:
      value = PERFORMANCE_METRICS_SPREADSHEET_HEADERS[colNumber].value;
      break;
    // Header subtitles
    case PERFORMANCE_METRICS_SPREADSHEET_HEADER_SUBTITLE:
      value = PERFORMANCE_METRICS_SPREADSHEET_HEADERS[colNumber].subtitle;
      break;

    case PERFORMANCE_METRICS_SPREADSHEET_RANK_ALIAS:
      {
        const rankValues = getRankExpression({ allTransactionsExpression });
        expr = rankValues.expr;
        hidden = rankValues.hidden;
        gridType = 'percentage';
      }
      break;
    // Rest of the rows (excluding Header titles and subtitles)
    default:
      // Parse cell based on Column number
      switch (colNumber) {
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.LTM_REVENUE:
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NTM_REVENUE:
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.LTM_EBITDA:
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NTM_EBITDA:
          format = largeCurrencyFormat;
          gridType = 'number';
          break;
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.LTM_REVENUE_GROWTH:
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.GROSS_MARGIN:
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.EBITDA_MARGIN:
          format = oneDecimalPercentFormat;
          gridType = 'percentage';
          break;

        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NTM_REVENUE_GROWTH:
          format = oneDecimalPercentFormat;
          gridType = 'percentage';
          expr = generateNTMRevenueGrowthExpression({ alphabet, rowNumber });
          value = 0;
          break;
        default:
          break;
      }
      break;
  }

  return {
    [key]: {
      ...row,
      alias,
      className,
      colSpan,
      columnLegend,
      expr: getExpression({ expr, columnLegend }),
      format,
      gridType,
      key,
      customKey,
      hidden,
      rowSpan,
      value,
      readOnly,
      useScalarSpreadsheetCell,
    } as Cell,
  };
};

const customParser = (params: CustomParserParams) => {
  const { columns, rowConfig } = params;

  let cells = {} as { [key: string]: Cell };
  const alphabet = alphabetGenerator([], columns.length) as string[];

  const allTransactionsExpression = generateAllRowsExpression({
    rowConfig: rowConfig.filter(row => !row.forceComponent),
    excludedRows: EXCLUDE_TITLE,
    excludeLastRow: true,
  });

  rowConfig.forEach((row, rowIndex: number) => {
    columns.forEach((column, colIndex: number) => {
      cells = {
        ...cells,
        ...cellParser({
          allTransactionsExpression,
          alphabet,
          colIndex,
          column,
          row,
          rowIndex,
        }),
      };
    });
  });

  return cells;
};

export default customParser;
